/**
 * Tier Detail View — standalone edit page for a single loyalty tier.
 *
 * Navigated to from ProgramSettingsTab tier list via ?tier=<index> search param.
 * Shows all editable fields for one tier: name, min_spent, collection_factor,
 * tier icon, tier card image. Will grow with more tier-specific features.
 *
 * OPS-1833 — Tier sub-page refactor
 */

import { useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, ImagePlus, X, Save, Pencil, Upload, icons } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { toast } from "sonner";
import { uploadFile } from "@/lib/erp/files";
import { RECOMMENDED_ICONS, ALL_TAB_ICONS } from "@/features/storefront/mobile-design/tab-icon-list";
import type { TierRow } from "../api/rewardsAdminHooks";
import { CardPreview } from "./card-designer/CardPreview";
import { DEFAULT_DESIGN } from "./card-designer/helpers";

/** Check if a tier_icon value is a Lucide icon name (e.g. "Star") vs an image URL. */
function isLucideIcon(value: string | null | undefined): boolean {
  if (!value) return false;
  return !value.startsWith("/") && !value.startsWith("http") && value in icons;
}

interface TierDetailViewProps {
  tier: TierRow;
  tierIndex: number;
  onBack: () => void;
  onSave: (index: number, updated: TierRow) => void;
  isSaving: boolean;
}

export function TierDetailView({
  tier,
  tierIndex,
  onBack,
  onSave,
  isSaving,
}: TierDetailViewProps) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<TierRow>({ ...tier });

  const update = (patch: Partial<TierRow>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  };

  const handleSave = () => {
    if (!draft.tier_name.trim()) {
      toast.error("กรุณาระบุชื่อเทียร์");
      return;
    }
    if (draft.collection_factor <= 0) {
      toast.error("อัตราสะสมต้องมากกว่า 0");
      return;
    }
    onSave(tierIndex, draft);
  };

  return (
    <div className="space-y-6 pt-4">
      {/* Back + header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={onBack}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0">
          <h3 className="text-lg font-semibold truncate">
            {draft.tier_name || "เทียร์ใหม่"}
          </h3>
          <p className="text-xs text-muted-foreground">
            แก้ไขรายละเอียดเทียร์
          </p>
        </div>
      </div>

      {/* Basic info */}
      <div>
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1 mb-3">
          ข้อมูลเทียร์
        </p>
        <Card className="rounded-2xl border-0">
          <CardContent className="divide-y px-5 py-7">
            <div className="space-y-2 pb-7">
              <Label>ชื่อเทียร์</Label>
              <Input
                value={draft.tier_name}
                onChange={(e) => update({ tier_name: e.target.value })}
                placeholder="เช่น Silver, Gold, Platinum"
              />
            </div>

            <div className="space-y-2 py-7">
              <Label>ยอดใช้จ่ายขั้นต่ำ (฿)</Label>
              <Input
                type="number"
                min={0}
                value={draft.min_spent}
                onChange={(e) =>
                  update({ min_spent: Math.max(0, Number(e.target.value) || 0) })
                }
              />
              <p className="text-xs text-muted-foreground">
                ลูกค้าต้องใช้จ่ายสะสมถึงจำนวนนี้เพื่อเลื่อนขึ้นเทียร์นี้
              </p>
            </div>

            <div className="space-y-2 pt-7">
              <Label>ทุก ฿ เท่าไหร่ ได้ 1 แต้ม</Label>
              <Input
                type="number"
                min={1}
                value={draft.collection_factor}
                onChange={(e) =>
                  update({
                    collection_factor: Math.max(1, Number(e.target.value) || 1),
                  })
                }
              />
              <p className="text-xs text-muted-foreground">
                อัตราสะสมแต้มของเทียร์นี้ ยิ่งตัวเลขน้อย ยิ่งได้แต้มเร็ว
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tier icon + Card designer */}
      <div>
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1 mb-3">
          รูปภาพและการ์ด
        </p>
        <Card className="rounded-2xl border-0">
          <CardContent className="divide-y px-5 py-7">
            {/* Tier icon — compact dual-mode: Lucide icon picker or upload */}
            <div className="flex items-center justify-between pb-7">
              <div className="flex items-center gap-3">
                <TierIconPreview value={draft.tier_icon} />
                <div>
                  <p className="text-sm font-medium">ไอคอนเทียร์</p>
                  <p className="text-xs text-muted-foreground">
                    แสดงข้างชื่อเทียร์ในหน้าร้าน
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <TierIconPicker
                  value={draft.tier_icon}
                  onChange={(v) => update({ tier_icon: v })}
                />
                {draft.tier_icon && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => update({ tier_icon: null })}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>

            {/* Card designer — centered preview with edit action */}
            <div className="pt-7">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm font-medium">การ์ดสมาชิก</p>
                  <p className="text-xs text-muted-foreground">
                    สี พื้นหลัง และลวดลายของการ์ดเทียร์นี้
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/rewards/card-designer?tier=${tierIndex}`)}
                >
                  <Pencil className="mr-1.5 h-3.5 w-3.5" />
                  ออกแบบ
                </Button>
              </div>
              <button
                type="button"
                onClick={() => navigate(`/rewards/card-designer?tier=${tierIndex}`)}
                className="group relative mx-auto block w-full max-w-[300px] cursor-pointer rounded-xl overflow-hidden ring-1 ring-black/5 transition-all hover:ring-black/15 hover:shadow-lg"
              >
                <CardPreview
                  design={draft.tier_card_design ? { ...DEFAULT_DESIGN, ...draft.tier_card_design } : DEFAULT_DESIGN}
                  tierName={draft.tier_name || "Tier Name"}
                  mini
                />
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Save */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="min-h-[44px]"
        >
          {isSaving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          บันทึก
        </Button>
      </div>
    </div>
  );
}

/* ── Tier Icon Preview (small circle showing current icon or image) ── */

function TierIconPreview({ value }: { value?: string | null }) {
  if (value && isLucideIcon(value)) {
    const Icon = icons[value as keyof typeof icons];
    return (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
        <Icon className="h-5 w-5 text-foreground" />
      </div>
    );
  }
  if (value) {
    return (
      <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full bg-muted">
        <img src={value} alt="tier icon" className="h-full w-full object-cover" />
      </div>
    );
  }
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
      <ImagePlus className="h-4 w-4 text-muted-foreground" />
    </div>
  );
}

/* ── Tier Icon Picker (popover with icon grid + upload tab) ── */

function TierIconPicker({
  value,
  onChange,
}: {
  value?: string | null;
  onChange: (v: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredIcons = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.toLowerCase();
    return ALL_TAB_ICONS.filter(
      (name) =>
        name.toLowerCase().includes(q) &&
        !RECOMMENDED_ICONS.includes(name as (typeof RECOMMENDED_ICONS)[number]),
    );
  }, [search]);

  function handleSelectIcon(name: string) {
    onChange(name);
    setOpen(false);
    setSearch("");
  }

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("ไฟล์ต้องมีขนาดไม่เกิน 2 MB");
      return;
    }
    setUploading(true);
    try {
      const res = await uploadFile(file, { isPrivate: false });
      onChange(res.file_url);
      setOpen(false);
    } catch {
      toast.error("อัปโหลดล้มเหลว");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const currentIconName = value && isLucideIcon(value) ? value : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <ImagePlus className="mr-1.5 h-3.5 w-3.5" />
          เปลี่ยนไอคอน
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={4} className="w-[280px] p-3">
        {/* Recommended icons grid */}
        <div className="mb-2 text-[11px] font-semibold text-muted-foreground">
          เลือกไอคอน
        </div>
        <div className="grid grid-cols-4 gap-1">
          {RECOMMENDED_ICONS.map((name) => {
            const Icon = icons[name as keyof typeof icons];
            if (!Icon) return null;
            return (
              <button
                key={name}
                type="button"
                onClick={() => handleSelectIcon(name)}
                className={`flex h-10 w-full items-center justify-center rounded-lg transition-colors ${
                  currentIconName === name
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-muted"
                }`}
                title={name}
              >
                <Icon className="h-5 w-5" strokeWidth={2} />
              </button>
            );
          })}
        </div>

        {/* Search */}
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="ค้นหาไอคอน..."
          className="mt-2 h-8 w-full rounded-lg border px-2.5 text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        />
        {search.trim() && (
          <div className="mt-2 max-h-[160px] overflow-y-auto">
            {filteredIcons.length === 0 ? (
              <p className="py-3 text-center text-xs text-muted-foreground">
                ไม่พบไอคอน
              </p>
            ) : (
              <div className="grid grid-cols-4 gap-1">
                {filteredIcons.map((name) => {
                  const Icon = icons[name as keyof typeof icons];
                  if (!Icon) return null;
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => handleSelectIcon(name)}
                      className={`flex h-10 w-full items-center justify-center rounded-lg transition-colors ${
                        currentIconName === name
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-muted"
                      }`}
                      title={name}
                    >
                      <Icon className="h-5 w-5" strokeWidth={2} />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Divider + Upload option */}
        <div className="my-2 border-t" />
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          อัปโหลดรูปเอง
        </button>
      </PopoverContent>
    </Popover>
  );
}
